import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, User, Trash2, AlertTriangle, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

/* Heuristic: name looks like a test/placeholder employee that should be scrubbed. */
const looksLikeTest = (n = "") => /^(test|todos|canales2026|h|a|prueba|qauser|dummy)([_\- ].*)?$/i.test(n.trim());

const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, danger = true, lang }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="employees-confirm-modal">
      <motion.div
        initial={{ scale: 0.9, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        className="brutal bg-white max-w-md w-full p-6"
      >
        <div className="flex items-start gap-3">
          {danger && <AlertTriangle className="w-6 h-6 text-canales-red shrink-0" />}
          <div className="flex-1">
            <div className="font-display font-black text-xl">{title}</div>
            <p className="mt-2 font-body text-sm">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            data-testid="employees-confirm-cancel"
            className="brutal-btn bg-white px-3 py-1.5 text-sm inline-flex items-center gap-1"
          >
            <X className="w-4 h-4" /> {lang === "es" ? "Cancelar" : "Cancel"}
          </button>
          <button
            onClick={onConfirm}
            data-testid="employees-confirm-ok"
            className={`brutal-btn ${danger ? "bg-canales-red" : "bg-canales-blue"} text-white px-3 py-1.5 text-sm inline-flex items-center gap-1`}
          >
            <Trash2 className="w-4 h-4" /> {lang === "es" ? "Borrar" : "Delete"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default function Employees() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { title, message, onOk }

  const fetchList = async () => {
    const { data } = await api.get("/admin/employees");
    setList(data);
  };

  useEffect(() => {
    fetchList();
  }, []);

  const openEmployee = async (name) => {
    const { data } = await api.get(`/admin/employees/${encodeURIComponent(name)}`);
    setSelected(data);
  };

  const toggle = (name) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAll = () => {
    if (checked.size === list.length) setChecked(new Set());
    else setChecked(new Set(list.map((e) => e.employee_name)));
  };

  const doBulkDelete = async (payload, successMsg) => {
    try {
      const { data } = await api.delete("/admin/employees", { data: payload });
      toast.success(
        successMsg ||
          (lang === "es"
            ? `Borrados ${data.attempts_deleted} intentos y ${data.reads_deleted} lecturas.`
            : `Deleted ${data.attempts_deleted} attempts and ${data.reads_deleted} reads.`)
      );
      setChecked(new Set());
      setSelected(null);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setConfirm(null);
    }
  };

  const askDeleteSelected = () => {
    const names = Array.from(checked);
    if (names.length === 0) return;
    setConfirm({
      title: lang === "es" ? `¿Borrar ${names.length} empleado(s)?` : `Delete ${names.length} employee(s)?`,
      message:
        lang === "es"
          ? `Se eliminarán TODOS los intentos de examen y lecturas de cómic de: ${names.join(", ")}. Esta acción es irreversible.`
          : `All exam attempts and comic reads for: ${names.join(", ")} will be removed. This cannot be undone.`,
      onOk: () => doBulkDelete({ names }),
    });
  };

  const askDeleteTests = () => {
    const tests = list.filter((e) => looksLikeTest(e.employee_name)).map((e) => e.employee_name);
    setConfirm({
      title: lang === "es" ? "Limpiar datos de prueba" : "Clean test data",
      message:
        lang === "es"
          ? `Borrará automáticamente registros con nombres tipo test/prueba/placeholder (${tests.length} encontrados${tests.length ? ": " + tests.join(", ") : ""}). Los usuarios reales no se tocan.`
          : `Automatically removes records with test/placeholder-looking names (${tests.length} found${tests.length ? ": " + tests.join(", ") : ""}). Real users are untouched.`,
      onOk: () => doBulkDelete({ test_only: true }),
    });
  };

  const askResetAll = () => {
    setConfirm({
      title: lang === "es" ? "⚠ Resetear TODO" : "⚠ Reset EVERYTHING",
      message:
        lang === "es"
          ? "Borrarás TODOS los intentos y lecturas de cómic de TODOS los empleados. Los exámenes, preguntas y cómics quedan intactos. Esta acción es irreversible."
          : "You will remove EVERY attempt and comic read from EVERY employee. Exams, questions and comics stay intact. Irreversible.",
      onOk: () => doBulkDelete({ reset_all: true }),
    });
  };

  const chartData = (selected?.attempts || [])
    .slice()
    .reverse()
    .map((a, i) => ({ n: i + 1, percent: a.score_percent }));

  const allChecked = list.length > 0 && checked.size === list.length;
  const testCount = useMemo(() => list.filter((e) => looksLikeTest(e.employee_name)).length, [list]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-4xl">{t("employees")}</h1>
        <div className="flex flex-wrap gap-2" data-testid="employees-toolbar">
          {testCount > 0 && (
            <button
              onClick={askDeleteTests}
              data-testid="employees-purge-tests"
              className="brutal-btn bg-canales-yellow text-black px-3 py-1.5 text-sm inline-flex items-center gap-1"
            >
              <Sparkles className="w-4 h-4" />
              {lang === "es" ? `Limpiar datos de prueba (${testCount})` : `Clean test data (${testCount})`}
            </button>
          )}
          <button
            onClick={askResetAll}
            data-testid="employees-reset-all"
            className="brutal-btn bg-black text-white px-3 py-1.5 text-sm inline-flex items-center gap-1"
          >
            <Trash2 className="w-4 h-4" />
            {lang === "es" ? "Resetear todo" : "Reset all"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="brutal bg-white p-4">
          {list.length === 0 ? (
            <p className="font-body text-neutral-600">{t("noAttempts")}</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 font-display font-bold text-sm">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    data-testid="employees-check-all"
                    className="w-4 h-4"
                  />
                  {lang === "es" ? "Seleccionar todos" : "Select all"}
                </label>
                {checked.size > 0 && (
                  <button
                    onClick={askDeleteSelected}
                    data-testid="employees-delete-selected"
                    className="brutal-btn bg-canales-red text-white px-3 py-1.5 text-sm inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    {lang === "es" ? `Borrar ${checked.size} seleccionado(s)` : `Delete ${checked.size} selected`}
                  </button>
                )}
              </div>
              <ul className="divide-y-2 divide-black">
                {list.map((e, i) => {
                  const isChecked = checked.has(e.employee_name);
                  const isTest = looksLikeTest(e.employee_name);
                  return (
                    <li key={e.employee_name}>
                      <div className={`w-full flex items-center gap-2 px-3 py-3 ${selected?.employee_name === e.employee_name ? "bg-canales-yellow" : "hover:bg-canales-cream"}`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggle(e.employee_name)}
                          data-testid={`employees-check-${e.employee_name}`}
                          className="w-4 h-4 shrink-0"
                        />
                        <button
                          onClick={() => openEmployee(e.employee_name)}
                          className="flex-1 text-left flex items-center justify-between"
                          data-testid={`employees-open-${e.employee_name}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-canales-blue text-white flex items-center justify-center font-display font-bold border-2 border-black">
                              #{i + 1}
                            </div>
                            <div>
                              <div className="font-display font-bold flex items-center gap-2">
                                {e.employee_name}
                                {isTest && (
                                  <span className="brutal-sm bg-canales-yellow text-black px-1.5 py-0.5 text-[10px] font-black tracking-widest uppercase">
                                    TEST
                                  </span>
                                )}
                              </div>
                              <div className="text-xs font-body text-neutral-600">
                                {e.attempts} {lang === "es" ? "intentos" : "attempts"} · {e.passed} {lang === "es" ? "aprobados" : "passed"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="brutal-sm px-2 py-1 bg-white font-display font-bold">{e.avg}%</span>
                            <ChevronRight className="w-4 h-4" />
                          </div>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="brutal bg-white p-4">
          {!selected ? (
            <div className="text-center text-neutral-500 py-16 font-body">
              <User className="w-10 h-10 mx-auto opacity-40" />
              <p className="mt-2">
                {lang === "es" ? "Elige un empleado para ver su progreso" : "Pick an employee to see their progress"}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display font-black text-2xl">{selected.employee_name}</h3>
                <button
                  onClick={() =>
                    setConfirm({
                      title: lang === "es" ? `Borrar ${selected.employee_name}` : `Delete ${selected.employee_name}`,
                      message:
                        lang === "es"
                          ? `Se eliminarán todos los intentos y lecturas de este empleado. Irreversible.`
                          : `All attempts and reads for this employee will be removed. Irreversible.`,
                      onOk: () => doBulkDelete({ names: [selected.employee_name] }),
                    })
                  }
                  data-testid="employees-delete-single"
                  className="brutal-btn bg-canales-red text-white px-2 py-1 text-xs inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {lang === "es" ? "Borrar" : "Delete"}
                </button>
              </div>
              <div className="mt-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="n" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="percent" stroke="#005BBB" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-4 space-y-2 max-h-64 overflow-auto">
                {selected.attempts.map((a) => (
                  <li key={a.attempt_id} className={`brutal-sm p-3 ${a.passed ? "bg-green-50" : "bg-red-50"}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-display font-bold">{lang === "es" ? a.exam_title_es : a.exam_title_en}</div>
                        <div className="text-xs font-body">{(a.submitted_at || "").slice(0, 19).replace("T", " ")}</div>
                      </div>
                      <div className="brutal-sm px-2 py-1 bg-white font-display font-bold">{a.score_percent}%</div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title || ""}
        message={confirm?.message || ""}
        onConfirm={confirm?.onOk}
        onCancel={() => setConfirm(null)}
        lang={lang}
      />
    </motion.div>
  );
}
